import { Asset } from '@graphcommerce/graphcms-ui'
import { FieldValues, useController, UseControllerProps } from '@graphcommerce/react-hook-form'
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Box, FormControlLabel, Radio, RadioGroup, Slider, Typography } from '@mui/material'
import { ConfiguratorQuestionFragment } from '../../graphql/configurator/ConfiguratorQuestion.gql'
import { ConfiguratorQuestionExplanation } from './ConfiguratorQuestionExplanation'

type ConfiguratorQuestionProps<TFieldValues extends FieldValues> = {
  configuratorQuestion: ConfiguratorQuestionFragment
  name: string
} & UseControllerProps<TFieldValues>

export function ConfiguratorQuestion<TFieldValues extends FieldValues>(
  props: ConfiguratorQuestionProps<TFieldValues>,
) {
  const { configuratorQuestion, ...controllerProps } = props
  const { answers, explanation, image, title, questionVariant } = configuratorQuestion

  const isDefaultQuestion = questionVariant === 'default'
  const isAgeQuestion = questionVariant === 'ageSlider'
  const isListQuestion = questionVariant === 'list'

  const {
    field: { value, onChange },
  } = useController({ ...controllerProps })

  if (!configuratorQuestion || !answers || answers.length < 1) return null

  const hasExtraInfo = explanation?.raw && explanation?.text

  const marks = answers.map((answer, i: number) => ({
    value: i + 1,
    label: answer.title,
    score: answer.score,
  }))

  return (
    <Box
      sx={(theme) => ({
        boxShadow: theme.shadows[8],
        background: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        py: theme.spacings.sm,
        px: { xs: theme.spacings.lg, md: theme.spacings.sm },
        display: 'flex',
        flexDirection: 'column',
        alignItems: image ? 'center' : 'start',
        gap: theme.spacings.xxs,
        position: 'relative',
      })}
    >
      {hasExtraInfo && <ConfiguratorQuestionExplanation title={title} explanation={explanation} />}
      {image && (
        <Asset sx={{ width: '150px', height: '150px', borderRadius: '100vw' }} asset={image} />
      )}
      <Typography variant='subtitle1'>{title}</Typography>

      {isAgeQuestion && (
        <Slider
          sx={(theme) => ({
            '& .MuiSlider-thumb': { background: theme.palette.primary.main },
            '& .MuiSlider-markLabel': {
              position: 'absolute',
              transform: 'translateX(-50%)',
              typography: 'body2',
              my: { xs: theme.spacings.md, md: theme.spacings.sm },
            },
            '& p.MuiSlider-markLabel:first-of-type': { transform: 'unset' },
            '& p.MuiSlider-markLabel:last-of-type': {
              transform: 'unset',
              left: 'auto !important',
              right: '0 !important',
            },
          })}
          aria-label='age-slider'
          step={1}
          marks={marks}
          value={value}
          slots={{ markLabel: 'p' }}
          onChange={onChange}
          min={1}
          max={answers.length}
        />
      )}

      {isListQuestion && (
        <RadioGroup value={value} onChange={(event) => onChange(Number(event.target.value))}>
          {marks.map((mark) => (
            <FormControlLabel
              key={mark.label}
              value={mark.value}
              control={<Radio />}
              label={<Typography variant='body2'>{mark.label}</Typography>}
            />
          ))}
        </RadioGroup>
      )}

      {isDefaultQuestion && (
        <Slider
          sx={(theme) => ({
            '& .MuiSlider-thumb': { background: theme.palette.primary.main },
            '& .MuiSlider-markLabel': {
              position: 'absolute',
              transform: 'translateX(-50%)',
              typography: 'body2',
              my: { xs: theme.spacings.md, md: theme.spacings.sm },
            },
            '& p.MuiSlider-markLabel:first-of-type': { transform: 'unset' },
            '& p.MuiSlider-markLabel:last-of-type': {
              transform: 'unset',
              left: 'auto !important',
              right: '0 !important',
            },
          })}
          aria-label='score-slider'
          step={1}
          marks={marks}
          min={1}
          max={answers.length}
          defaultValue={1}
          value={value}
          onChange={onChange}
          slots={{ markLabel: 'p' }}
        />
      )}
    </Box>
  )
}
